'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { useAnalytics, type SearchCheckInTooSoonDetails } from '@contexts/use-analytics';

/**
 * Reports a dated search that returned nothing because the check-in date sits
 * inside the platform booking lead time (BHT-8058). Pass the details while
 * that empty state is on screen, and `null` otherwise.
 *
 * The copy alone is not monitorable, so both surfaces that render it emit
 * `search_check_in_too_soon`. That makes turned-away demand queryable in GA4
 * without screenshots, and it is the only way a drift between the floor this
 * client believes and the one listing_engine enforces becomes visible: if the
 * backend floor grows, guests keep landing on the generic no-inventory copy
 * and this event stays quiet for dates it should cover.
 *
 * Taking the payload or nothing, rather than a flag beside a widened
 * `checkInDate`, puts the decision at the call site that already knows both
 * and leaves no impossible state for this hook to guard against.
 */
export function useCheckInTooSoonReport(details) {
  const $ = _c(9);
  const {
    emitSearchCheckInTooSoonEvent
  } = useAnalytics();
  let t0;
  if ($[0] !== details) {
    t0 = details ?? {};
    $[0] = details;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    checkInDate,
    checkOutDate,
    firstBookableCheckInDate,
    surface
  } = t0;
  let t1;
  let t2;
  if ($[2] !== checkInDate || $[3] !== checkOutDate || $[4] !== emitSearchCheckInTooSoonEvent || $[5] !== firstBookableCheckInDate || $[6] !== surface) {
    t1 = () => {
      if (!checkInDate || !firstBookableCheckInDate || !surface) {
        return;
      }
      emitSearchCheckInTooSoonEvent({
        checkInDate,
        checkOutDate: checkOutDate ?? null,
        firstBookableCheckInDate,
        surface
      });
    };
    t2 = [checkInDate, checkOutDate, firstBookableCheckInDate, surface, emitSearchCheckInTooSoonEvent];
    $[2] = checkInDate;
    $[3] = checkOutDate;
    $[4] = emitSearchCheckInTooSoonEvent;
    $[5] = firstBookableCheckInDate;
    $[6] = surface;
    $[7] = t1;
    $[8] = t2;
  } else {
    t1 = $[7];
    t2 = $[8];
  }
  React.useEffect(t1, t2);
}